import cn from "classnames";
import { useDebouncedValue } from "hooks";
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import {
  Controller,
  FieldError,
  useFieldArray,
  useFormContext
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button, FormField, Input } from "ui";

import { useCheckBarcodeAvailability } from "../../../../../apis";
import {
  DEFAULT_RIBBON_WIDTH_MM,
  generateBarcodeValue,
  printBarcodeLabel
} from "../../../../../helpers";
import { BarcodePreview } from "../../../../BarcodePreview";
import { ProductFormValues } from "../../ProductDetailsForm.types";
import { VariantBarcodesBlockProps } from "./VariantBlock.types";

import styles from "../../ProductDetailsForm.module.scss";

interface BarcodeRowProps {
  variantIndex: number;
  barcodeIndex: number;
  sizeLabel: string;
  originalValue: string;
  collectAllBarcodes: () => string[];
  showLabel: boolean;
  ribbonWidthMm: number;
}

const MAX_GENERATE_ATTEMPTS = 10;

const BarcodeRow = ({
  variantIndex,
  barcodeIndex,
  sizeLabel,
  originalValue,
  collectAllBarcodes,
  showLabel,
  ribbonWidthMm
}: BarcodeRowProps) => {
  const { t } = useTranslation();
  const { control, watch, setError, clearErrors } =
    useFormContext<ProductFormValues>();

  const fieldName =
    `variants.${variantIndex}.barcodes.${barcodeIndex}.value` as const;

  const currentValue = watch(fieldName) ?? "";
  const debouncedValue = useDebouncedValue(currentValue, 600);

  const checkAvailability = useCheckBarcodeAvailability();

  useEffect(() => {
    const trimmed = debouncedValue.trim();
    if (!trimmed || trimmed === originalValue) {
      return;
    }

    checkAvailability.mutate(
      { values: [trimmed] },
      {
        onSuccess: ({ taken }) => {
          if (currentValue.trim() !== trimmed) return;

          if (taken.includes(trimmed)) {
            setError(fieldName, {
              type: "barcodeTaken",
              message: t("This barcode is already in use")
            });
          } else {
            clearErrors(fieldName);
          }
        }
      }
    );
  }, [debouncedValue, originalValue]);

  return (
    <Controller
      control={control}
      name={fieldName}
      render={({
        field: { value: barcodeValue, onChange },
        fieldState: { error }
      }) => {
        const generateAndSet = () => {
          const existing = new Set(collectAllBarcodes().filter(Boolean));
          existing.delete(barcodeValue);
          let candidate = generateBarcodeValue();
          let attempts = 0;
          while (existing.has(candidate) && attempts < MAX_GENERATE_ATTEMPTS) {
            candidate = generateBarcodeValue();
            attempts += 1;
          }
          onChange(candidate);
        };

        const handlePrint = () => {
          if (barcodeValue?.trim()) {
            printBarcodeLabel(barcodeValue.trim(), { ribbonWidthMm });
          }
        };

        return (
          <div className={styles.productForm__barcodeField}>
            <span className={styles.productForm__barcodeField__size}>
              {t(sizeLabel)}
            </span>

            <div className={styles.productForm__barcodeField__main}>
              <Input
                label={showLabel ? t("Barcode") : undefined}
                value={barcodeValue ?? ""}
                onChange={onChange}
                errorMessage={error?.message}
              />

              <div className={styles.productForm__barcodeField__buttons}>
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  onClick={generateAndSet}
                >
                  {t("Generate")}
                </Button>
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  onClick={handlePrint}
                  disabled={!barcodeValue?.trim()}
                >
                  {t("Print")}
                </Button>
              </div>

              {barcodeValue?.trim() ? (
                <div className={styles.productForm__barcodeField__preview}>
                  <BarcodePreview value={barcodeValue.trim()} />
                </div>
              ) : null}
            </div>
          </div>
        );
      }}
    />
  );
};

export const VariantBarcodesBlock = ({
  variantIndex,
  defaultValuesBarcodes
}: VariantBarcodesBlockProps) => {
  const { t } = useTranslation();
  const { control, watch, clearErrors, getValues } =
    useFormContext<ProductFormValues>();
  const sizes = watch("sizes");

  const [ribbonWidthMm, setRibbonWidthMm] = useState<number>(
    DEFAULT_RIBBON_WIDTH_MM
  );
  const [widthInput, setWidthInput] = useState<string>(
    String(DEFAULT_RIBBON_WIDTH_MM)
  );

  const commitWidth = () => {
    const parsed = parseFloat(widthInput);
    if (Number.isFinite(parsed) && parsed > 0 && parsed <= 1000) {
      setRibbonWidthMm(parsed);
      setWidthInput(String(parsed));
    } else {
      setWidthInput(String(ribbonWidthMm));
    }
  };

  const {
    fields: barcodes,
    append: appendBarcode,
    remove: removeBarcode
  } = useFieldArray({
    control,
    name: `variants.${variantIndex}.barcodes`
  });

  const getSizeLabelBySizeAttributeValueId = (sizeAttributeValueId: string) => {
    return (
      sizes.find((size) => size.sizeAttributeValueId === sizeAttributeValueId)
        ?.label || t("Unknown size")
    );
  };

  const clearErrorAboutDupicated = (barcodesError: FieldError | undefined) => {
    if (barcodesError?.type === "uniqueBarcodes") {
      clearErrors(`variants.${variantIndex}.barcodes`);
    }
  };

  const collectAllBarcodes = () => {
    const variants = getValues("variants") ?? [];
    const all: string[] = [];
    for (const variant of variants) {
      for (const barcode of variant.barcodes ?? []) {
        if (barcode.value) all.push(barcode.value.trim());
      }
    }
    return all;
  };

  const originalByVariantValue = useMemo(() => {
    const map = new Map<string, string>();
    for (const barcode of defaultValuesBarcodes ?? []) {
      if (barcode.value) {
        map.set(barcode.sizeAttributeValueId, barcode.value);
      }
    }
    return map;
  }, [defaultValuesBarcodes]);

  const addSizeBarcodes = () => {
    const addingSize = sizes.find((size) =>
      barcodes.every(
        (barcode) => barcode.sizeAttributeValueId !== size.sizeAttributeValueId
      )
    )?.sizeAttributeValueId;

    if (addingSize) {
      const existingBarcode = defaultValuesBarcodes?.find(
        (barcode) => barcode.sizeAttributeValueId === addingSize
      );

      appendBarcode(
        existingBarcode ?? { sizeAttributeValueId: addingSize, value: "" }
      );
    }
  };

  const removeSizeBarcode = () => {
    const removingIndex = barcodes.findIndex((barcode) =>
      sizes.every(
        ({ sizeAttributeValueId }) =>
          sizeAttributeValueId !== barcode.sizeAttributeValueId
      )
    );

    removeBarcode(removingIndex);
  };

  useEffect(() => {
    if (sizes.length === barcodes.length) {
      return;
    }

    if (sizes.length > barcodes.length) {
      addSizeBarcodes();
    } else {
      removeSizeBarcode();
    }
  }, [sizes]);

  return (
    <div className={styles.productForm_barcodesBlock}>
      <div className={styles.productForm__ribbonWidth}>
        <Input
          label={t("Ribbon width (mm)")}
          type="number"
          min={1}
          value={widthInput}
          onChange={(e) => setWidthInput(e.target.value)}
          onBlur={commitWidth}
        />
      </div>

      <Controller
        control={control}
        name={`variants.${variantIndex}.barcodes`}
        render={({ fieldState: { error: barcodesError } }) => (
          <FormField errorMessage={barcodesError?.message}>
            <div
              className={cn(styles.productForm__block)}
              onChangeCapture={() => clearErrorAboutDupicated(barcodesError)}
            >
              {barcodes.length > 0 ? (
                barcodes.map((barcodeItem, barcodeIndex) => {
                  const sizeAttributeValueId =
                    barcodeItem.sizeAttributeValueId;
                  return (
                    <BarcodeRow
                      key={barcodeItem.id}
                      variantIndex={variantIndex}
                      barcodeIndex={barcodeIndex}
                      sizeLabel={getSizeLabelBySizeAttributeValueId(
                        sizeAttributeValueId
                      )}
                      originalValue={
                        originalByVariantValue.get(sizeAttributeValueId) ?? ""
                      }
                      collectAllBarcodes={collectAllBarcodes}
                      showLabel={barcodeIndex === 0}
                      ribbonWidthMm={ribbonWidthMm}
                    />
                  );
                })
              ) : (
                <div className={styles.productForm__barcodesPlaceholer}>
                  {t("Please, add some sizes for this product")}
                </div>
              )}
            </div>
          </FormField>
        )}
      />
    </div>
  );
};
